import { isAbortOrCanceledError } from "@mirage-web/utils/abort-signals";
import { useLatestRef } from "@mirage-web/utils/react-helpers";
import { getErrorMessage } from "@mirage-web/utils/scoped";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";

import { ApiHost } from "./api-client";
import { NetworkingLayerConfig } from "./config";
import { createSSEStreamConnection, SSEEvent } from "./sse-stream";

export interface SSEReconnectOptions {
  enabled?: boolean;
  initialDelayMs?: number;
  maxDelayMs?: number;
  factor?: number;
  maxAttempts?: number;
}

export interface UseSSEStreamResult {
  isConnected: boolean;
  error: Error | null;
  disconnect: () => void;
}

export type SSEEventSchemasMap = Record<string, z.ZodTypeAny>;

export type SSEParsedEvent<EventSchemas extends SSEEventSchemasMap> = {
  [EventName in Extract<keyof EventSchemas, string>]: {
    name: EventName;
    payload: z.infer<EventSchemas[EventName]>;
    rawEvent: SSEEvent;
  };
}[Extract<keyof EventSchemas, string>];

const DEFAULT_RECONNECT: Required<SSEReconnectOptions> = {
  enabled: true,
  initialDelayMs: 1000,
  maxDelayMs: 30000,
  factor: 2,
  maxAttempts: Number.POSITIVE_INFINITY,
};

export interface UseOnSSEStreamEventsOptions<
  ParamsSchema extends z.ZodTypeAny,
  EventSchemas extends SSEEventSchemasMap,
> {
  config: NetworkingLayerConfig;
  host: ApiHost;
  path: string;
  pathParams?: Record<string, string>;
  paramsSchema?: ParamsSchema;
  params?: z.input<ParamsSchema>;
  headers?: Record<string, string>;
  enabled?: boolean;
  reconnect?: SSEReconnectOptions;
  events: EventSchemas;
  onEvent: (event: SSEParsedEvent<EventSchemas>) => void;
  onError?: (error: Error) => void;
  /** When set, persists Last-Event-ID in sessionStorage under this key so reconnects within the same browser session resume instead of replaying the full event history. */
  sessionStorageKey?: string;
}

export function useOnStreamEvents<
  ParamsSchema extends z.ZodTypeAny,
  EventSchemas extends SSEEventSchemasMap,
>({
  config,
  host,
  path,
  pathParams,
  paramsSchema,
  params,
  headers,
  enabled = true,
  reconnect,
  events,
  onEvent,
  onError,
  sessionStorageKey,
}: UseOnSSEStreamEventsOptions<ParamsSchema, EventSchemas>): UseSSEStreamResult {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const lastEventIdRef = useRef<string | undefined>(readSessionLastEventId(sessionStorageKey));
  const manualDisconnectRef = useRef(false);
  const connectionRef = useRef<ReturnType<typeof createSSEStreamConnection> | null>(null);
  const onEventRef = useLatestRef(onEvent);
  const onErrorRef = useLatestRef(onError);
  const eventsRef = useLatestRef(events);
  const headersRef = useLatestRef(headers);
  const pathParamsRef = useLatestRef(pathParams);
  const paramsRef = useLatestRef(params);
  const pathParamsKey = JSON.stringify(pathParams);
  const paramsKey = JSON.stringify(params);

  const reconnectOptions = useMemo(
    () => ({
      enabled: reconnect?.enabled ?? DEFAULT_RECONNECT.enabled,
      initialDelayMs: reconnect?.initialDelayMs ?? DEFAULT_RECONNECT.initialDelayMs,
      maxDelayMs: reconnect?.maxDelayMs ?? DEFAULT_RECONNECT.maxDelayMs,
      factor: reconnect?.factor ?? DEFAULT_RECONNECT.factor,
      maxAttempts: reconnect?.maxAttempts ?? DEFAULT_RECONNECT.maxAttempts,
    }),
    [
      reconnect?.enabled,
      reconnect?.factor,
      reconnect?.initialDelayMs,
      reconnect?.maxAttempts,
      reconnect?.maxDelayMs,
    ]
  );

  const disconnect = useCallback(() => {
    manualDisconnectRef.current = true;
    connectionRef.current?.disconnect();
    connectionRef.current = null;
    setIsConnected(false);
  }, []);

  useEffect(() => {
    if (!enabled) {
      disconnect();
      return;
    }

    manualDisconnectRef.current = false;
    setError(null);

    let cancelled = false;
    let reconnectDelay = reconnectOptions.initialDelayMs;
    let reconnectAttempts = 0;

    const run = async () => {
      while (!cancelled) {
        let parsedParams: Record<string, unknown> | undefined;

        try {
          parsedParams = paramsSchema
            ? paramsSchema.parse(paramsRef.current)
            : (paramsRef.current as Record<string, unknown>);
        } catch (parseError) {
          const err = toError(parseError);
          setError(err);
          onErrorRef.current?.(err);
          return;
        }

        const connection = createSSEStreamConnection(config, {
          host,
          path,
          pathParams: pathParamsRef.current,
          params: parsedParams,
          headers: headersRef.current,
          lastEventId: lastEventIdRef.current,
          onOpen: () => {
            reconnectAttempts = 0;
            reconnectDelay = reconnectOptions.initialDelayMs;
            setIsConnected(true);
            setError(null);
          },
          onClose: () => {
            setIsConnected(false);
          },
          onEvent: (rawEvent) => {
            const eventName = rawEvent.event ?? "message";
            const schema = eventsRef.current[eventName];
            if (!schema) {
              return;
            }

            if (rawEvent.id) {
              lastEventIdRef.current = rawEvent.id;
              writeSessionLastEventId(sessionStorageKey, rawEvent.id);
            }

            try {
              const parsedPayload = schema.parse(JSON.parse(rawEvent.data));
              onEventRef.current({
                name: eventName,
                payload: parsedPayload,
                rawEvent,
              } as SSEParsedEvent<EventSchemas>);
            } catch (eventError) {
              const err = toError(eventError);
              setError(err);
              onErrorRef.current?.(err);
            }
          },
        });

        connectionRef.current = connection;

        try {
          await connection.connect();
          reconnectDelay = reconnectOptions.initialDelayMs;
        } catch (streamError) {
          const err = toError(streamError);
          setIsConnected(false);

          if (cancelled || manualDisconnectRef.current || isAbortOrCanceledError(err)) {
            return;
          }

          setError(err);
          onErrorRef.current?.(err);
        } finally {
          connectionRef.current = null;
        }

        if (cancelled || manualDisconnectRef.current || !reconnectOptions.enabled || !enabled) {
          return;
        }

        if (reconnectAttempts >= reconnectOptions.maxAttempts) {
          return;
        }

        reconnectAttempts += 1;
        await sleep(reconnectDelay);
        reconnectDelay = Math.min(
          reconnectOptions.maxDelayMs,
          Math.ceil(reconnectDelay * reconnectOptions.factor)
        );
      }
    };

    void run();

    return () => {
      cancelled = true;
      disconnect();
    };
  }, [
    config,
    disconnect,
    enabled,
    host,
    paramsKey,
    paramsRef,
    pathParamsKey,
    pathParamsRef,
    paramsSchema,
    path,
    reconnectOptions,
    eventsRef,
    headersRef,
    onEventRef,
    onErrorRef,
    sessionStorageKey,
  ]);

  return {
    isConnected,
    error,
    disconnect,
  };
}

function sleep(ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });
}

function toError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }
  return new Error(getErrorMessage(error), { cause: error });
}

function readSessionLastEventId(key: string | undefined): string | undefined {
  if (!key) {
    return;
  }
  try {
    return sessionStorage.getItem(key) ?? undefined;
  } catch {
    return;
  }
}

function writeSessionLastEventId(key: string | undefined, eventId: string): void {
  if (!key) {
    return;
  }
  try {
    sessionStorage.setItem(key, eventId);
  } catch {
    // sessionStorage may be unavailable (private browsing, storage full, etc.)
  }
}
